import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { artifactsApi } from "../api/applications";
import { ApiError } from "../api/client";
import { ARTIFACT_LABELS, type Artifact, type ArtifactKind } from "../api/types";
import { Alert, Button, Spinner, Textarea } from "./ui";

const PDF_FOR: Partial<Record<ArtifactKind, ArtifactKind>> = {
  resume_md: "resume_pdf",
  cover_letter_md: "cover_letter_pdf",
};

// What the PDF adds from the profile (backend/app/pipeline/letterhead.py), so
// nobody edits a header into the text only to see it replaced.
const LETTERHEAD_NOTE: Partial<Record<ArtifactKind, string>> = {
  resume_md:
    "Your name, contact details and references are added to the PDF from your profile, " +
    "replacing any written here.",
  cover_letter_md:
    "Your name and contact details, and the sign-off, are added to the PDF from your " +
    "profile, replacing any written here.",
};

/**
 * One generated document: read it, edit it, see its PDF.
 *
 * Saving an edit creates a new revision rather than overwriting, and marks
 * only that document's PDF stale — which is why the re-render button appears
 * on its own rather than offering to regenerate the whole chain.
 */
export default function DocumentPanel({
  applicationId,
  artifact,
  artifacts,
  onRegenerate,
  busy,
}: {
  applicationId: string;
  artifact: Artifact;
  artifacts: Artifact[];
  onRegenerate: (kinds: ArtifactKind[]) => void;
  busy: boolean;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);

  const { data, isPending, refetch } = useQuery({
    queryKey: ["artifact", artifact.id],
    queryFn: () => artifactsApi.read(artifact.id),
  });

  useEffect(() => setDraft(null), [artifact.id]);

  // A save comes back as a new artifact with its own id, so the old query
  // would keep serving the pre-edit text. Seed the new revision's cache and
  // refetch the application so the parent hands us the new artifact; the
  // draft stays on screen until then and is cleared by the id change above.
  const save = useMutation({
    mutationFn: (text: string) => artifactsApi.save(artifact.id, text),
    onSuccess: async (saved) => {
      queryClient.setQueryData(["artifact", saved.id], saved);
      if (saved.id === artifact.id) {
        setDraft(null);
        await refetch();
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["application", applicationId] }),
        queryClient.invalidateQueries({ queryKey: ["applications"] }),
      ]);
    },
  });

  if (isPending || !data) return <Spinner label="Loading document…" />;

  const pdfKind = PDF_FOR[artifact.kind];
  const pdf = pdfKind ? artifacts.find((a) => a.kind === pdfKind) : undefined;
  const body = draft ?? data.content_text ?? "";
  const dirty = draft !== null && draft !== data.content_text;
  const saveError = save.error
    ? save.error instanceof ApiError
      ? save.error.message
      : "Your edit could not be saved."
    : null;

  return (
    <div className="space-y-4">
      {artifact.state === "stale" && (
        <Alert kind="error">
          This was built from an earlier version of your profile or of the documents above
          it. Regenerate to bring it up to date.
        </Alert>
      )}

      {data.error_log && (
        <div>
          <Alert kind="error">This document failed to generate.</Alert>
          <pre className="mt-2 max-h-40 overflow-auto rounded-md bg-gray-50 p-3 text-xs whitespace-pre-wrap text-gray-700">
            {data.error_log}
          </pre>
        </div>
      )}

      {saveError && <Alert kind="error">{saveError}</Alert>}

      {LETTERHEAD_NOTE[artifact.kind] && (
        <p className="text-xs text-gray-500">
          {LETTERHEAD_NOTE[artifact.kind]}{" "}
          <Link to="/profile" className="text-accent-700 hover:underline">
            Edit your profile
          </Link>
        </p>
      )}

      <Textarea
        rows={24}
        value={body}
        onChange={(e) => setDraft(e.target.value)}
        spellCheck
      />

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => save.mutate(body)} disabled={!dirty || save.isPending}>
          {save.isPending ? "Saving…" : "Save edit"}
        </Button>

        <Button
          variant="secondary"
          onClick={() => onRegenerate([artifact.kind])}
          disabled={busy}
        >
          Regenerate with Claude
        </Button>

        {pdf && (
          <>
            <a href={artifactsApi.downloadUrl(pdf.id)} download>
              <Button variant="secondary" disabled={pdf.state === "failed"}>
                Download PDF
              </Button>
            </a>
            {(pdf.state !== "fresh" || dirty) && (
              <Button
                variant="secondary"
                onClick={() => onRegenerate([pdf.kind])}
                disabled={busy || dirty}
                title={dirty ? "Save your edit first" : undefined}
              >
                Re-render PDF
              </Button>
            )}
          </>
        )}

        <span className="text-xs text-gray-500">
          Revision {data.revision}
          {data.edited_by_user && " · edited by you"}
        </span>
      </div>

      {pdf && pdf.state !== "failed" && (
        <details className="rounded-lg border border-gray-200">
          <summary className="cursor-pointer px-4 py-2 text-sm font-medium text-gray-800">
            Preview {ARTIFACT_LABELS[pdf.kind]}
          </summary>
          <iframe
            title={ARTIFACT_LABELS[pdf.kind]}
            src={artifactsApi.inlineUrl(pdf.id)}
            className="h-[70vh] w-full rounded-b-lg border-t border-gray-200"
          />
        </details>
      )}
    </div>
  );
}
